import type { Type } from '@angular/core';
import type { Route } from '@angular/router';
import type { BrandingComponent } from '@features/partners/builder/settings/branding/branding.component';
import type { NpsComponent } from '@features/partners/builder/settings/nps/nps.component';
import type { SignMethodsPage } from '@features/partners/builder/settings/sign-methods/sign-methods.page';
import type { SupportComponent } from '@features/partners/builder/settings/support/support.component';
import type { TrackersRouteComponent } from '@features/partners/builder/settings/trackers/trackers.component';
import type { VersionsComponent } from '@features/partners/builder/settings/versions/versions.component';
import type { WidgetComponent } from '@features/partners/builder/settings/widget/widget.component';

import { CommunicationsWebservice } from '@features/partners/builder/content/communication/services/communications.webservice';
import { canDeactivateGuard } from '@features/partners/builder/routes/canDeactivate.guard';
import { SETTINGS_ROUTING } from '@features/partners/builder/settings/routes';
import { signMethodsResolver } from '@features/partners/builder/settings/sign-methods/resolvers/sign-methods.resolver';
import { partnerIdResolver } from '@features/partners/builder/settings/support/support.resolver';
import { trackersResolver } from '@features/partners/builder/settings/trackers/trackers.resolver';
import { versionsGuard } from '@features/partners/builder/settings/versions/versions.guard';

const PROFILE_ROUTES = async (): Promise<Route[]> =>
  (await import('@features/partners/builder/settings/profile/routes/profile.routes')).PROFILE_ROUTES;

const BrandingFeature = async (): Promise<Type<BrandingComponent>> =>
  (await import('@features/partners/builder/settings/branding/branding.component')).BrandingComponent;

const MENU_ROUTES = async (): Promise<Route[]> =>
  (await import('@features/partners/builder/settings/menu/routes/menu.routes')).MENU_ROUTES;

const VersionsFeature = async (): Promise<Type<VersionsComponent>> =>
  (await import('@features/partners/builder/settings/versions/versions.component')).VersionsComponent;

const SupportFeature = async (): Promise<Type<SupportComponent>> =>
  (await import('@features/partners/builder/settings/support/support.component')).SupportComponent;

const PLATFORM_ACCESS_ROUTES = async (): Promise<Route[]> =>
  (await import('@features/partners/builder/settings/platform-access/routes')).PLATFORM_ACCESS_ROUTES;

const NpsFeature = async (): Promise<Type<NpsComponent>> =>
  (await import('@features/partners/builder/settings/nps/nps.component')).NpsComponent;

const TrackersFeature = async (): Promise<Type<TrackersRouteComponent>> =>
  (await import('@features/partners/builder/settings/trackers/trackers.component')).TrackersRouteComponent;

const WidgetFeature = async (): Promise<Type<WidgetComponent>> =>
  (await import('@features/partners/builder/settings/widget/widget.component')).WidgetComponent;

const SignMethodsFeature = async (): Promise<Type<SignMethodsPage>> =>
  (await import('@features/partners/builder/settings/sign-methods/sign-methods.page')).SignMethodsPage;

export const SETTINGS_ROUTES: Route[] = [
  {
    path: '',
    redirectTo: SETTINGS_ROUTING.Branding,
    pathMatch: 'full',
  },
  {
    path: SETTINGS_ROUTING.Profile,
    title: SETTINGS_ROUTING.Profile,
    loadChildren: PROFILE_ROUTES,
  },
  {
    path: SETTINGS_ROUTING.Branding,
    title: SETTINGS_ROUTING.Branding,
    loadComponent: BrandingFeature,
    canDeactivate: [canDeactivateGuard],
  },
  {
    path: SETTINGS_ROUTING.Menu,
    loadChildren: MENU_ROUTES,
  },
  {
    path: SETTINGS_ROUTING.Versions,
    loadComponent: VersionsFeature,
    canActivate: [versionsGuard],
  },
  {
    path: SETTINGS_ROUTING.Support,
    title: SETTINGS_ROUTING.Support,
    loadComponent: SupportFeature,
    providers: [CommunicationsWebservice],
    canDeactivate: [canDeactivateGuard],
    resolve: {
      partnerId: partnerIdResolver,
    },
  },
  {
    path: SETTINGS_ROUTING.PlatformAccess,
    title: SETTINGS_ROUTING.PlatformAccess,
    loadChildren: PLATFORM_ACCESS_ROUTES,
  },
  {
    path: SETTINGS_ROUTING.Nps,
    title: SETTINGS_ROUTING.Nps,
    loadComponent: NpsFeature,
    canDeactivate: [canDeactivateGuard],
  },
  {
    path: SETTINGS_ROUTING.Trackers,
    loadComponent: TrackersFeature,
    canDeactivate: [canDeactivateGuard],
    resolve: {
      trackers: trackersResolver,
    },
  },
  {
    path: SETTINGS_ROUTING.SignMethods,
    loadComponent: SignMethodsFeature,
    resolve: {
      signMethodsData: signMethodsResolver,
    },
  },
  {
    path: SETTINGS_ROUTING.Widget,
    title: SETTINGS_ROUTING.Widget,
    loadComponent: WidgetFeature,
    canDeactivate: [canDeactivateGuard],
  },
];
